import { last } from 'lodash';
import { SECTION_FIELD_ALIAS } from '../constants/OneRealMortgageConstants';
import { DropboxResponse } from '../openapi/dropbox';
import {
  ContentContentTypeEnum,
  ContentItem,
  FileReference,
  LicenseBoardMLSResponse,
  OnboardingResponseRoleEnum,
  QuestionAnswersResponse,
  UserResponse,
} from '../openapi/yenta';
import { formatPhoneNumber } from './StringUtils';

/**
 * Helper function to get application ID from agent onboarding data
 * @param authUserDetail - Auth user details containing onboarding information
 * @returns Application ID or undefined if not found
 */
export const getLegacyApplicationId = (
  authUserDetail: UserResponse | undefined,
  role: OnboardingResponseRoleEnum,
): string | undefined => {
  const agentOnboarding = authUserDetail?.onboardings?.find(
    (application: any) => application.role === role,
  );

  return agentOnboarding?.legacyApplicationId || undefined;
};

/**
 * Maps option labels that exceed character limit to description field
 * @param options - Array of options with label and description
 * @param characterLimit - Maximum characters before moving label to description (default: 40)
 * @returns Modified options array with long labels moved to description
 */
export const mapLongOptionLabelsToDescription = (
  options: any[] | undefined,
  characterLimit: number = 40,
): any[] | undefined => {
  if (!options) return options;

  const hasLongLabels = options.some(
    (option) => option.label && option.label.length > characterLimit,
  );

  if (!hasLongLabels) return options;

  return options.map((option) => ({
    ...option,
    description:
      option.label && option.label.length > characterLimit
        ? option.label
        : option.description,
    label:
      option.label && option.label.length > characterLimit
        ? undefined
        : option.label,
  }));
};

/**
 * Formats a date string to MM/DD/YY format
 * @param dateString - The date string to format (e.g., "2001-12-07")
 * @returns Formatted date string in MM/DD/YY format
 */
export const formatDateToMMDDYYYY = (dateString: string): string => {
  try {
    const date = new Date(dateString);
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');
    const year = String(date.getUTCFullYear());
    return `${month}/${day}/${year}`;
  } catch {
    return dateString;
  }
};

/**
 * Formats country enum value to display name
 * @param country - The country enum value (e.g., "UNITED_STATES")
 * @returns Formatted country name
 */
export const formatCountryName = (country: string): string => {
  const countryMap: Record<string, string> = {
    UNITED_STATES: 'United States',
    CANADA: 'Canada',
  };

  return countryMap[country] || country;
};

/**
 * Formats a list of states to comma-separated string
 * @param states - Array of state strings
 * @returns Formatted string of states
 */
export const formatStatesList = (states: string[]): string => {
  if (!Array.isArray(states) || states.length === 0) return '';

  // Format each state name properly (e.g., "CALIFORNIA" -> "California")
  const formattedStates = states.map((state) => formatStateProvince(state));

  return formattedStates.join(', ');
};

/**
 * Helper function to format answer based on type for display
 * @param answer - The answer value to format
 * @param answerType - The type of the answer
 * @returns Formatted answer value for display
 */
export const formatAnswerForDisplay = (
  answer: any,
  answerType?: string,
): any => {
  if (answer === null || answer === undefined) return null;

  // Handle phone type - format to (XXX) XXX-XXXX
  if (answerType === 'PHONE' && answer?.value) {
    return formatPhoneNumber(answer.value);
  }

  // Handle email type
  if (answerType === 'EMAIL' && answer?.value) {
    return answer.value;
  }

  // Handle DATE type - format to MM/DD/YYYY
  if (answerType === 'DATE' && answer) {
    return formatDateToMMDDYYYY(answer);
  }

  // Handle LIST type (multi-select) - format states list
  if (answerType === 'LIST' && Array.isArray(answer)) {
    // Check if it's a states list (contains state names in uppercase)
    const isStatesList = answer.some(
      (item) => typeof item === 'string' && item === item.toUpperCase(),
    );

    if (isStatesList) {
      return formatStatesList(answer);
    }

    return answer.join(', ');
  }

  // Handle STRING type - check for special cases
  if (answerType === 'STRING' && typeof answer === 'string') {
    // Check if it's a country value
    if (answer === 'UNITED_STATES' || answer === 'CANADA') {
      return formatCountryName(answer);
    }

    // Check if it's a single state value
    if (answer === answer.toUpperCase() && answer.includes('_')) {
      return formatStateProvince(answer);
    }
  }

  return answer;
};

/**
 * Helper function to extract file info from question with fileReferences
 * @param answerData - The answer data containing fileReferences
 * @param dropbox - The dropbox response containing file metadata
 * @returns File info object or null
 */
const getFileInfoFromAnswerData = (
  answerData: any,
  dropbox?: DropboxResponse,
): any => {
  // Check if there are file references at the question level
  const fileReferences = answerData?.fileReferences?.references;
  if (!fileReferences || fileReferences.length === 0) {
    return null;
  }

  // Get the last file reference (most recent upload)
  const lastFile = last(fileReferences) as FileReference;
  if (!lastFile?.fileId) {
    return null;
  }

  // Try to find the actual filename from dropbox files
  let filename = 'Document'; // Default placeholder
  if (dropbox?.files) {
    const dropboxFile = dropbox.files.find(
      (file) => file.id === lastFile.fileId,
    );
    if (dropboxFile?.filename) {
      filename = dropboxFile.filename;
    }
  }

  // If still using placeholder, check if we can infer from the answer alias
  if (filename === 'Document' && answerData?.answers) {
    const fileAnswer = answerData.answers.find(
      (a: any) => a.type === 'FILE_REQUEST',
    );
    if (fileAnswer?.alias) {
      // Provide more descriptive placeholders based on field type
      if (fileAnswer.alias.includes('DriversLicense')) {
        filename = 'Driver License';
      } else if (fileAnswer.alias.includes('RealEstateLicense')) {
        filename = 'Real Estate License';
      }
    }
  }

  // Return file info object for ReviewFieldValue to handle
  return {
    fileId: lastFile.fileId,
    filename: filename,
    isFile: true,
  };
};

/**
 * Helper function to get formatted answers for a question
 * @param questionId - The ID of the question
 * @param questionAnswers - The answers response from API
 * @param dropbox - The dropbox response containing file metadata
 * @returns Formatted answer string or null
 */
export const getQuestionAnswers = (
  questionId: string,
  questionAnswers: QuestionAnswersResponse | undefined,
  dropbox?: DropboxResponse,
): any => {
  if (!questionAnswers?.answersByQuestionAlias) return null;

  // Find the answer data by matching itemId
  const answerData = Object.values(questionAnswers.answersByQuestionAlias).find(
    (item) => item.itemId === questionId,
  );

  if (!answerData?.answers || answerData.answers.length === 0) {
    return null;
  }

  // Check if this question has file uploads
  const hasFileUpload = answerData.answers.some(
    (ans: any) => ans.type === 'FILE_REQUEST',
  );

  // If it has file uploads and fileReferences exist, handle specially
  if (
    hasFileUpload &&
    answerData.fileReferences?.references &&
    answerData.fileReferences.references.length > 0
  ) {
    const fileAnswer = answerData.answers.find(
      (ans: any) => ans.type === 'FILE_REQUEST',
    );

    // Only return file info if the answer is true (file was uploaded)
    if (fileAnswer?.answer === true) {
      return getFileInfoFromAnswerData(answerData, dropbox);
    }
    return '-'; // File field exists but no file uploaded
  }

  // Filter out null answers and certification toggles for display
  const validAnswers = answerData.answers.filter((ans: any) => {
    // Skip null answers
    if (ans.answer === null) return false;
    // Skip certification toggles
    if (ans.alias && ans.alias.includes('Certification')) return false;
    // Skip FILE_REQUEST if already handled above
    if (ans.type === 'FILE_REQUEST') return false;
    return true;
  });

  if (validAnswers.length === 0) return null;
  if (validAnswers.length === 1) {
    return formatAnswerForDisplay(
      validAnswers[0].answer,
      validAnswers[0].answerType,
    );
  }

  // For all multi-field questions, return array of answers with labels
  // Format: "Label: Answer" for each item, displayed with 4px gap
  return validAnswers.map((a: any) => {
    const formattedAnswer = formatAnswerForDisplay(a.answer, a.answerType);
    if (a.label && a.label !== a.answer) {
      return `${a.label}: ${formattedAnswer}`;
    }
    return formattedAnswer;
  });
};

// Helper function to format state/province codes to display names
export const formatStateProvince = (stateCode: string): string => {
  // Convert state codes like 'CALIFORNIA' to 'California'
  // or 'NEW_YORK' to 'New York'
  return stateCode
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

/**
 * Extracts state name from a title string
 * @param title - Title string like "Real Estate License (Optional): Hawaii"
 * @returns State name after the colon, or null if not found
 */
export const extractStateFromTitle = (
  title: string | undefined,
): string | null => {
  if (!title) return null;

  // Look for pattern with colon separator
  const colonIndex = title.lastIndexOf(':');
  if (colonIndex === -1) return null;

  // Extract and trim the state name after the colon
  const stateName = title.substring(colonIndex + 1).trim();
  return stateName || null;
};

/**
 * Gets the license number for a specific state from doesBusinessInExtended data
 * @param doesBusinessInExtended - Array of license/board/MLS data from application
 * @param stateName - The state name to search for (e.g., "Hawaii")
 * @returns License number for the state, or null if not found
 */
export const getLicenseNumberByState = (
  doesBusinessInExtended: LicenseBoardMLSResponse[] | undefined,
  stateName: string | null,
): string | null => {
  if (!doesBusinessInExtended || !stateName) return null;

  // Normalize the state name for comparison (convert to uppercase and replace spaces with underscores)
  const normalizedStateName =
    stateName.toUpperCase().replace(/\s+/g, '_') || '';

  // Find the entry for the specified state
  const stateEntry = doesBusinessInExtended.find((entry) => {
    const stateOrProvince =
      entry.licenseResponse?.administrativeArea?.stateOrProvince;
    if (!stateOrProvince) return false;

    // Compare normalized versions
    const normalizedStateOrProvince =
      stateOrProvince.toUpperCase().replace(/\s+/g, '_') || '';

    return normalizedStateOrProvince === normalizedStateName;
  });

  // Return the license number if found
  return stateEntry?.licenseResponse?.number || null;
};

/**
 * Checks if the country field value has changed from the existing answer
 * @param formData - The current form data being submitted
 * @param currentQuestion - The current question object with fields
 * @returns true if the country value has changed, false otherwise
 */
export const hasCountryFieldChanged = (
  formData: any,
  currentQuestion: any,
): boolean => {
  const countryFieldId = getCountryFieldId(currentQuestion);
  if (!countryFieldId) return true; // If no country field found, proceed normally

  const existingAnswer = getCountryFieldAnswer(currentQuestion);
  const newAnswer = formData[countryFieldId];

  // If there's no existing answer, consider it as changed
  if (!existingAnswer) return true;

  return existingAnswer !== newAnswer;
};

/**
 * Gets the country field ID from the current question
 * @param currentQuestion - The current question object with fields
 * @returns The field ID of the country field, or null if not found
 */
export const getCountryFieldId = (currentQuestion: any): string | null => {
  const countryField = currentQuestion?.content?.fields?.find(
    (field: any) => field.alias === SECTION_FIELD_ALIAS.COUNTRY_FIELD,
  );
  return countryField?.id || null;
};

/**
 * Gets the existing answer for the country field
 * @param currentQuestion - The current question object with fields
 * @returns The existing answer value for the country field, or null if not found
 */
export const getCountryFieldAnswer = (currentQuestion: any): string | null => {
  const countryField = currentQuestion?.content?.fields?.find(
    (field: any) => field.alias === SECTION_FIELD_ALIAS.COUNTRY_FIELD,
  );
  return countryField?.answer || null;
};

/**
 * Checks if the current question is the "Create Account" section intro
 * This is used to determine when to track the application started event
 * @param legacyApplicationId - The legacy application ID
 * @param currentQuestion - The current question/content item
 * @returns true if it's the Create Account section intro with valid applicationId, false otherwise
 */
export const isCreateAccountSectionIntro = (
  legacyApplicationId: string | undefined,
  currentQuestion: ContentItem | undefined,
): boolean => {
  return (
    !!legacyApplicationId &&
    !!currentQuestion &&
    currentQuestion?.content?.contentType ===
      ContentContentTypeEnum.SectionIntro &&
    currentQuestion?.sectionName === 'Create Account'
  );
};
